import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from './Icon';
import { cx } from './cx';

export type ToastTone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

const TONES: Record<ToastTone, { icon: IconName; ring: string; mark: string }> = {
  success: { icon: 'checkCircle', ring: 'ring-emerald-200', mark: 'text-emerald-600' },
  error: { icon: 'alert', ring: 'ring-red-200', mark: 'text-red-600' },
  info: { icon: 'info', ring: 'ring-brand-200', mark: 'text-brand-600' },
};

/** How long a message stays up. Errors linger — they usually ask for a decision. */
const DISMISS_MS: Record<ToastTone, number> = { success: 4000, info: 5000, error: 8000 };

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Confirmation that something happened.
 *
 * The application had no such component at all: saving a job, withdrawing an
 * application and approving an attendance month all completed in silence, so
 * the only way to tell a click had worked was to notice the data change. On a
 * slow connection that gap is long enough for people to click again.
 *
 * A toast is for the outcome of something the user just did, and nothing else.
 * Anything that must be acted on belongs in the page — a toast is gone in
 * seconds and cannot be recovered.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);
  // Cleared on unmount so a timer cannot fire into a torn-down tree, which in
  // StrictMode's double-mount would otherwise warn on every toast in dev.
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = (nextId.current += 1);
      // Three at a time. A stack tall enough to cover the control that produced
      // it stops being feedback and starts being an obstacle.
      setToasts((current) => [...current.slice(-2), { id, tone, message }]);

      const timer = setTimeout(() => {
        timers.current.delete(timer);
        dismiss(id);
      }, DISMISS_MS[tone]);
      timers.current.add(timer);
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/*
        A single persistent live region, rendered whether or not there is
        anything in it. Screen readers only announce changes inside a region
        that already existed — mounting the region together with its first
        message means that message is never read out.
      */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end"
      >
        {toasts.map((toast) => {
          const tone = TONES[toast.tone];
          return (
            <div
              key={toast.id}
              // Errors interrupt; confirmations wait their turn.
              role={toast.tone === 'error' ? 'alert' : 'status'}
              className={cx(
                'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg bg-white p-3',
                'shadow-lg ring-1 motion-safe:animate-toast-in',
                tone.ring,
              )}
            >
              <Icon name={tone.icon} size="lg" className={cx('mt-0.5', tone.mark)} />
              <p className="min-w-0 flex-1 text-sm text-ink-800">{toast.message}</p>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label="Dismiss"
                className="grid h-6 w-6 shrink-0 place-items-center rounded text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700 focus-visible:outline focus-visible:outline-2"
              >
                <Icon name="close" size="xs" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Throws when used outside the provider rather than returning a no-op.
 *
 * A silent no-op would mean a confirmation that never appears and nothing to
 * explain why — the exact failure the component exists to prevent.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside a <ToastProvider>');
  return api;
}
